"use client";

import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSession } from "next-auth/react";
import { toast } from "sonner";

import { acceptInviteExistingUser } from "@/actions/invite-user";

const AcceptExistingUserInvite = () => {
  const session = useSession();
  const queryClient = useQueryClient();
  const [invite, setInvite] = useState<{
    token: string;
    inviteSignature: string;
  } | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 1; // Only retry once to prevent infinite loops

  useEffect(() => {
    // Only access sessionStorage after component mounts on client
    if (typeof window !== "undefined" && session.data?.user?.email) {
      const inviteSignature = `invt_${btoa(session.data.user.email)}`;
      const storedToken = sessionStorage.getItem(
        `invt_${btoa(session.data.user.email)}`,
      );

      if (storedToken) {
        setInvite({
          token: storedToken,
          inviteSignature,
        });
      }
    }
  }, [session.data?.user?.email]);

  useEffect(() => {
    // Only process if we have an invite, not currently processing, and haven't exceeded max retries
    // Also wait for session to be fully loaded (status === "authenticated") to ensure OAuth flow completed
    if (
      invite &&
      !isProcessing &&
      retryCount <= MAX_RETRIES &&
      session.status === "authenticated"
    ) {
      // Add a small delay to ensure cookies are set after OAuth callback
      // This is especially important for Microsoft OAuth where cookies are set in JWT callback
      const processInvite = async () => {
        // Wait a bit for cookies to be available (OAuth callback might still be setting them)
        await new Promise((resolve) => setTimeout(resolve, 500));

        setIsProcessing(true);
        acceptInviteExistingUser(invite.token)
          .then(async (response) => {
            if (response?.success) {
              toast.success(response.success);
              // Clear the token after successful acceptance
              sessionStorage.removeItem(invite.inviteSignature);
              setInvite(null); // Clear invite state to prevent re-processing
              setRetryCount(0); // Reset retry count
              // Invalidate and refetch the user query to refresh orgId
              await queryClient.invalidateQueries({
                queryKey: ["getLoggedUser"],
              });
              // Wait for the query to refetch
              await queryClient.refetchQueries({
                queryKey: ["getLoggedUser"],
              });
            } else if (response?.error) {
              // Determine if this is a permanent error that shouldn't be retried
              const isPermanentError =
                response.error === "Invalid token" ||
                response.error === "Token expired" ||
                response.error === "Invitation not found" ||
                response.error === "User not found in database" ||
                response.error === "Unable to get user information from API";

              // Check if this is a transient error that should be retried (like auth not ready)
              const isTransientError =
                response.error?.includes("Authentication not ready") ||
                response.error?.includes("Please wait");

              if (
                isPermanentError ||
                (retryCount >= MAX_RETRIES && !isTransientError)
              ) {
                // Clear immediately for permanent errors or after max retries (unless it's a transient error)
                toast.error(response.error);
                sessionStorage.removeItem(invite.inviteSignature);
                setInvite(null);
                setRetryCount(0);
              } else if (isTransientError) {
                // For transient errors (like auth not ready), don't show error toast
                // Just increment retry count and wait - the delay will help
                setRetryCount((prev) => prev + 1);
              } else {
                // For other transient errors, show error and increment retry count
                toast.error(response.error);
                setRetryCount((prev) => prev + 1);
              }
            }
          })
          .catch((error) => {
            console.error("Error accepting invite:", error);
            // Clear on catch if we've exceeded max retries
            if (retryCount >= MAX_RETRIES) {
              toast.error("Failed to accept invitation");
              sessionStorage.removeItem(invite.inviteSignature);
              setInvite(null);
              setRetryCount(0);
            } else {
              toast.error("Failed to accept invitation");
              setRetryCount((prev) => prev + 1);
            }
          })
          .finally(() => {
            setIsProcessing(false);
          });
      };

      processInvite();
    } else if (invite && retryCount > MAX_RETRIES) {
      // If we've exceeded max retries, clear everything to prevent any further attempts
      sessionStorage.removeItem(invite.inviteSignature);
      setInvite(null);
      setRetryCount(0);
    }
  }, [invite, isProcessing, queryClient, retryCount, session.status]);

  return null;
};

export default AcceptExistingUserInvite;
